import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode, FormEvent } from 'react';

import type { StyleType } from './styled';
import {
  Cancel,
  CancelWrapper,
  SearchOverlayWrapper,
  ScrollPane,
  SearchFieldWrapper,
} from './styled';
import SearchFieldPostfix from '@components/SearchFieldPostfix';
import { Textfield } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';
import { focusAndOpenKeyboard } from '@components/Search/components/SearchOverlay/focusAndOpenKeyboard';

type Theme = 'Light' | 'Dark';

async function setStatusBarTheme(theme: Theme) {
  try {
    if (RUNTIME_ENV === 'native') {
      const { Style, StatusBar } = await import('@capacitor/status-bar');
      await StatusBar.setStyle({
        style: Style[theme],
      });
    }
  } catch (error) {
    console.log(error);
  }
}

interface Props {
  name?: string;
  onChange?: (query: string) => void;
  onClose?: () => void;
  onReset?: () => void;
  placeholder?: string;
  prefix?: ReactNode;
  query?: string;
  status?: 'success' | 'pending' | 'error';
  styleType?: StyleType;
  children?: ReactNode;
  isFetching?: boolean;
}

export const SearchOverlay = ({
  styleType = 'dark',
  query = '',
  name = 'search-overlay-search-field',
  onChange,
  onClose,
  onReset,
  status,
  prefix,
  placeholder,
  children,
  isFetching,
}: Props) => {
  const { trackEvent } = useTracking();
  const input = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState(query as string);

  useEffect(() => {
    if (RUNTIME_ENV === 'native') {
      if (styleType === 'light') {
        setStatusBarTheme('Light');
      }
      return () => {
        if (styleType === 'light') {
          setStatusBarTheme('Dark');
        }
      };
    }
  }, [styleType]);

  useLayoutEffect(() => {
    const { current } = input;
    if (current) {
      focusAndOpenKeyboard(current, 300);
    }
  }, []);

  const handleChange = (e: FormEvent<HTMLInputElement>) => {
    setSearchQuery(e.currentTarget.value);
    onChange && onChange(e.currentTarget.value);
  };

  const handleReset = () => {
    const { current } = input;
    if (current) current.focus();
    setSearchQuery('');
    onChange && onChange('');
    onReset && onReset();
    trackEvent({ action: 'reset' });
  };
  return (
    <SearchOverlayWrapper styleType={styleType}>
      <CancelWrapper>
        <Cancel
          styleType={
            styleType === 'light' ? 'gentleLargeReverse' : 'gentleLarge'
          }
          display="block"
          onClick={(e) => {
            e.stopPropagation();
            trackEvent({ action: 'close' });
            onClose && onClose();
          }}
          data-cy-id="search-overlay-cancel"
        >
          Cancel
        </Cancel>
      </CancelWrapper>
      <SearchFieldWrapper>
        <Textfield
          ref={input}
          name={name}
          type="search"
          styleType={styleType === 'light' ? 'standardReverse' : 'standard'}
          value={searchQuery}
          placeholder={placeholder}
          autoFocus
          onFocus={() => trackEvent({ action: 'focus' })}
          prefix={prefix}
          postfix={
            <SearchFieldPostfix
              styleType={styleType}
              searchQuery={searchQuery}
              onReset={handleReset}
              searchStatus={status}
              isFetching={isFetching}
            />
          }
          onChange={handleChange}
          autoComplete="off"
        />
      </SearchFieldWrapper>
      <ScrollPane>
        <ul data-cy-id="search-overlay-results-list">{children}</ul>
      </ScrollPane>
    </SearchOverlayWrapper>
  );
};
